/* eslint-disable react-refresh/only-export-components */
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { Separator } from '@eigtech/ui/components/separator'
import { cn } from '@eigtech/ui/lib/utils'
import { ButtonGroupContext, ButtonGroupContextType } from './button/button-group-context'

const buttonGroupVariants = cva(
  "flex w-fit items-stretch *:focus-visible:relative *:focus-visible:z-10 has-[>[data-slot=button-group]]:gap-2 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal: '',
        vertical: 'flex-col',
      },
      mode: {
        attached: '',
        split: 'gap-2',
      },
    },
    compoundVariants: [
      {
        mode: 'attached',
        orientation: 'horizontal',
        className:
          '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none',
      },
      {
        mode: 'attached',
        orientation: 'vertical',
        className:
          'flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none',
      },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      mode: 'split',
    },
  }
)

export type ButtonGroupVariants = VariantProps<typeof buttonGroupVariants>

export type ButtonGroupProps = React.ComponentProps<'div'> &
  ButtonGroupVariants &
  ButtonGroupContextType

function ButtonGroup({
  appearance,
  autoHeight,
  className,
  disabled,
  mode,
  orientation,
  placeholder,
  radius,
  size,
  underline,
  underlined,
  variant,
  ...props
}: ButtonGroupProps) {
  const context: ButtonGroupContextType = {
    appearance,
    autoHeight,
    disabled,
    mode,
    placeholder,
    radius,
    size,
    underline,
    underlined,
    variant,
  }
  return (
    <ButtonGroupContext.Provider value={context}>
      <div
        className={cn(buttonGroupVariants({ orientation }), className)}
        data-orientation={orientation}
        data-slot="button-group"
        role="group"
        {...props}
      />
    </ButtonGroupContext.Provider>
  )
}

function ButtonGroupText({ className, render, ...props }: useRender.ComponentProps<'div'>) {
  return useRender({
    defaultTagName: 'div',
    render,
    props: mergeProps<'div'>(
      {
        className: cn(
          "bg-muted shadow-xs flex items-center gap-2 rounded-md border px-4 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
          className
        ),
      },
      props
    ),
  })
}

function ButtonGroupSeparator({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn(
        'bg-input m-0! relative self-stretch data-[orientation=vertical]:h-auto',
        className
      )}
      data-slot="button-group-separator"
      orientation={orientation}
      {...props}
    />
  )
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants }
